import { memo, useCallback } from 'react';

import { ChatActions } from '@/components/v2/conversation/ChatActions';
import { cn } from '@/lib/utils';
import type { SnapshotAction } from '@shared/v2-schemas';

export interface ChatActionsCarouselProps {
  actions: SnapshotAction[];
  onSendMessage: (text: string) => void;
  onNavigate: (href: string) => void;
  onNavigateAndSend: (href: string, text: string) => void;
  onWidgetAction: (args: { widget_id: string; action: string; text?: string }) => void;
  className?: string;
  /** Visual variant forwarded to each pill. `'glass'` (default) for snapshot pills; `'outlined'` for per-turn pills. */
  variant?: 'glass' | 'outlined';
}

function ChatActionsCarouselImpl({
  actions,
  onSendMessage,
  onNavigate,
  onNavigateAndSend,
  onWidgetAction,
  className,
  variant = 'glass',
}: ChatActionsCarouselProps) {
  const dispatch = useCallback(
    (action: SnapshotAction) => {
      switch (action.kind) {
        case 'send_message':
          onSendMessage(action.text);
          return;
        case 'navigate':
          onNavigate(action.href);
          return;
        case 'navigate_and_send':
          onNavigateAndSend(action.href, action.text);
          return;
        case 'widget_action':
          onWidgetAction({
            widget_id: action.widget_id,
            action: action.action,
            text: action.text,
          });
          return;
        default: {
          const _exhaustive: never = action;
          throw new Error(`Unhandled SnapshotAction kind: ${JSON.stringify(_exhaustive)}`);
        }
      }
    },
    [onSendMessage, onNavigate, onNavigateAndSend, onWidgetAction],
  );

  if (actions.length === 0) return null;

  return (
    <div
      data-testid="chat-actions-carousel"
      role="group"
      aria-label="Suggested actions"
      className={cn(
        'flex gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:thin]',
        className,
      )}
    >
      {actions.map((action, index) => (
        <ChatActions
          key={`${action.kind}-${index}-${action.title}`}
          iconSize={action.iconSize}
          iconName={action.iconName}
          title={action.title}
          description={action.description}
          onClick={() => dispatch(action)}
          // 019 FR-028: a `primary` action renders as the distinct filled CTA,
          // regardless of the carousel's default variant.
          variant={action.primary ? 'primary' : variant}
        />
      ))}
    </div>
  );
}

export const ChatActionsCarousel = memo(ChatActionsCarouselImpl);
